import AsyncStorage from '@react-native-async-storage/async-storage';

const MILESTONES_STORAGE_KEY = '@cache_shown_milestones';
const SHARE_TOOLTIP_KEY = '@cache_share_tooltip_shown';

export const ITEM_MILESTONES = [1, 5, 10, 25, 50, 100];
export const VALUE_MILESTONES = [500, 1000, 5000, 10000, 25000, 50000, 100000];

export interface Milestone {
  type: 'items' | 'value';
  value: number;
  message: string;
  emoji: string;
}

const MILESTONE_MESSAGES: Record<string, { message: string; emoji: string }> = {
  // Item milestones
  'items_1': { message: "You've started your collection!", emoji: '🎉' },
  'items_5': { message: 'Your collection is growing!', emoji: '📈' },
  'items_10': { message: 'Double digits!', emoji: '🔥' },
  'items_25': { message: 'Serious collector status', emoji: '💎' },
  'items_50': { message: 'Half a century of items!', emoji: '🏆' },
  'items_100': { message: 'Triple digits!', emoji: '👑' },
  // Value milestones
  'value_500': { message: 'Your first $500!', emoji: '💵' },
  'value_1000': { message: 'Your first thousand!', emoji: '🎯' },
  'value_5000': { message: 'Five grand club!', emoji: '💰' },
  'value_10000': { message: 'Five figures!', emoji: '🚀' },
  'value_25000': { message: 'Quarter of $100k!', emoji: '⭐' },
  'value_50000': { message: 'Halfway to six figures!', emoji: '🌟' },
  'value_100000': { message: 'Six figure collection!', emoji: '👑' },
};

export async function getShownMilestones(): Promise<Set<string>> {
  try {
    const stored = await AsyncStorage.getItem(MILESTONES_STORAGE_KEY);
    return stored ? new Set(JSON.parse(stored)) : new Set();
  } catch {
    return new Set();
  }
}

export async function markMilestoneShown(milestoneKey: string): Promise<void> {
  try {
    const shown = await getShownMilestones();
    shown.add(milestoneKey);
    await AsyncStorage.setItem(MILESTONES_STORAGE_KEY, JSON.stringify([...shown]));
  } catch (error) {
    console.error('Failed to save milestone:', error);
  }
}

export async function checkForNewMilestone(
  itemCount: number,
  totalValue: number
): Promise<Milestone | null> {
  const shownMilestones = await getShownMilestones();

  // Check item milestones (highest first)
  for (const milestone of [...ITEM_MILESTONES].reverse()) {
    const key = `items_${milestone}`;
    if (itemCount >= milestone && !shownMilestones.has(key)) {
      const { message, emoji } = MILESTONE_MESSAGES[key] || { message: 'Milestone reached!', emoji: '🎉' };
      return { type: 'items', value: milestone, message, emoji };
    }
  }

  // Check value milestones (highest first)
  for (const milestone of [...VALUE_MILESTONES].reverse()) {
    const key = `value_${milestone}`;
    if (totalValue >= milestone && !shownMilestones.has(key)) {
      const { message, emoji } = MILESTONE_MESSAGES[key] || { message: 'Milestone reached!', emoji: '🎉' };
      return { type: 'value', value: milestone, message, emoji };
    }
  }

  return null;
}

export function formatMilestoneValue(milestone: Milestone): string {
  if (milestone.type === 'items') {
    return `${milestone.value} ${milestone.value === 1 ? 'item' : 'items'}`;
  }
  if (milestone.value >= 1000) {
    return `$${milestone.value / 1000}k`;
  }
  return `$${milestone.value}`;
}

export async function hasSeenShareTooltip(): Promise<boolean> {
  try {
    const seen = await AsyncStorage.getItem(SHARE_TOOLTIP_KEY);
    return seen === 'true';
  } catch {
    return false;
  }
}

export async function markShareTooltipSeen(): Promise<void> {
  try {
    await AsyncStorage.setItem(SHARE_TOOLTIP_KEY, 'true');
  } catch (error) {
    console.error('Failed to save tooltip state:', error);
  }
}

export interface Achievement {
  key: string;
  type: 'items' | 'value';
  value: number;
  title: string;
  description: string;
  emoji: string;
  unlocked: boolean;
}

export async function getAllAchievements(
  currentItemCount: number,
  currentTotalValue: number
): Promise<Achievement[]> {
  const achievements: Achievement[] = [];

  // Item achievements
  for (const milestone of ITEM_MILESTONES) {
    const key = `items_${milestone}`;
    const { message, emoji } = MILESTONE_MESSAGES[key] || { message: 'Milestone reached!', emoji: '🎉' };
    achievements.push({
      key,
      type: 'items',
      value: milestone,
      title: `${milestone} ${milestone === 1 ? 'Item' : 'Items'}`,
      description: message,
      emoji,
      unlocked: currentItemCount >= milestone,
    });
  }

  // Value achievements
  for (const milestone of VALUE_MILESTONES) {
    const key = `value_${milestone}`;
    const { message, emoji } = MILESTONE_MESSAGES[key] || { message: 'Milestone reached!', emoji: '🎉' };
    const formattedValue = milestone >= 1000 ? `$${milestone / 1000}k` : `$${milestone}`;
    achievements.push({
      key,
      type: 'value',
      value: milestone,
      title: formattedValue,
      description: message,
      emoji,
      unlocked: currentTotalValue >= milestone,
    });
  }

  return achievements;
}
